import * as React from 'react'
import * as XState from 'xstate'
import * as XStateReact from '@xstate/react'
import { isEqual } from 'lodash'
import * as Toaster from 'contexts/toaster'
import SubmitMachine from '../machines/submit'
import * as SubmissionClient from 'clients/min/submission'
import * as Auth from 'contexts/authentication'
import * as Resolver from '../resolver'
import * as Env from 'env'

export function useMachine(initialValues, Store, schema) {
    const { isAuthenticated, user, loginWithRedirect } = Auth.useAuth()
    const isAuthenticatedRef = React.useRef(isAuthenticated)
    const save = SubmissionClient.useSaveSubmission()
    const toaster = Toaster.useContext()

    React.useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated
    }, [isAuthenticated])

    return XStateReact.useMachine(Root, {
        context: {
            values: useInitialContextValues(initialValues, Store, schema),
            ownsObservations: initialValues ? user?.sub === initialValues.userID : true,
        },
        actions: {
            scrollTop() {
                // FIXME Should not have this check...need a better implementation!
                if (Env.server) {
                    return
                }

                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                })
            },
            mergeDefaultValues: XState.assign({
                values: createContextValues,
            }),
            store: XState.assign({
                values(context, event) {
                    const { values } = event.payload

                    Store.set(values)

                    return {
                        ...context.values,
                        current: values,
                    }
                },
            }),
            backup: XState.assign({
                values(context) {
                    const { values } = context

                    return {
                        ...values,
                        backup: values.current,
                    }
                },
            }),
            reset(context, event) {
                Store.clear()

                toaster.clear() // FIXME Machine should be better
                toaster.push(event.payload.notification)
            },
            undo(context) {
                const { backup } = context.values

                Store.set(backup)

                toaster.pop()
            },
            clear() {
                Store.clear()
            },
        },
        guards: {
            isDirty(context) {
                const { reset, current } = context.values

                return !isEqual(reset, current)
            },
            shouldJumpToPreview(context) {
                // FIXME Should not have this check...need a better implementation!
                if (Env.server) {
                    return false
                }

                const params = new URLSearchParams(window.location.search)

                return params.get('state') === 'preview'
            },
        },
        services: {
            submit() {
                return SubmitMachine.withConfig({
                    services: {
                        authenticate() {
                            const redirectUri =
                                document.location.origin + '/mountain-information-network/submit'

                            return loginWithRedirect({
                                redirectUri,
                                appState: {
                                    returnTo: redirectUri,
                                    params: {
                                        state: 'preview',
                                    },
                                },
                            })
                        },
                        save(context) {
                            return save(context.values)
                        },
                    },
                    guards: {
                        isAuthenticated() {
                            return isAuthenticatedRef.current
                        },
                    },
                })
            },
        },
    })
}

// Machine
const Root = XState.Machine({
    id: 'submission',
    initial: 'form',
    context: {
        completed: new Set(),
    },
    states: {
        form: {
            entry: 'scrollTop',
            initial: 'idle',
            on: {
                '': {
                    target: 'preview',
                    cond: 'shouldJumpToPreview',
                },
                CHANGED: {
                    target: '.idle',
                    actions: ['store'],
                },
                MERGE_DEFAULT_VALUES: {
                    target: '.idle',
                    actions: [
                        'mergeDefaultValues',
                        createResetFormAction(context => context.values.current),
                    ],
                },
            },
            states: {
                idle: {
                    always: [
                        {
                            target: 'dirty',
                            cond: 'isDirty',
                        },
                        {
                            target: 'clean',
                        },
                    ],
                },
                clean: {
                    on: {
                        UNDO: {
                            target: 'dirty',
                            actions: [
                                'undo',
                                createResetFormAction(context => context.values.backup),
                                'scrollTop',
                            ],
                        },
                    },
                },
                dirty: {
                    on: {
                        SUBMIT: 'submitted',
                        RESET: {
                            target: 'clean',
                            actions: [
                                'backup',
                                'reset',
                                createResetFormAction(context => context.values.reset),
                                'scrollTop',
                            ],
                        },
                    },
                },
                submitted: {
                    type: 'final',
                    entry: XState.assign({
                        values(context, event) {
                            return {
                                ...context.values,
                                current: event.payload.values,
                            }
                        },
                        completed() {
                            return new Set(['form'])
                        },
                    }),
                },
            },
            onDone: 'preview',
        },
        preview: {
            entry: 'scrollTop',
            on: {
                PREVIOUS: 'form',
                NEXT: {
                    target: 'submit',
                    actions: XState.assign({
                        completed(context) {
                            return context.completed.add('preview')
                        },
                    }),
                },
            },
        },
        submit: {
            entry: 'scrollTop',
            invoke: {
                id: 'submit',
                src: 'submit',
                data: {
                    values(context) {
                        return context.values.current
                    },
                },
                onDone: {
                    target: 'success',
                    actions: XState.assign({
                        submissionID(context, event) {
                            return event.data.submissionID
                        },
                    }),
                },
            },
            on: {
                PREVIOUS: 'preview',
            },
        },
        success: {
            type: 'final',
            entry: ['scrollTop', 'clear'],
        },
    },
})

// Utils
function createResetFormAction(getValuesFromContext) {
    return XState.send(context => {
        return {
            type: 'RESET_FORM',
            payload: {
                values: getValuesFromContext(context),
            },
        }
    })
}
function createContextValues({ values }, event) {
    const { initial } = values
    const defaults = Object.assign({}, values.defaults, event?.payload?.values)

    return {
        initial,
        defaults,
        reset: Object.assign({}, defaults, initial),
        current: Object.assign({}, defaults, initial, values.current),
    }
}
function useInitialContextValues(initialValues, Store, schema) {
    return React.useMemo(() => {
        const date = new Date()

        // Clear unwanted values from initial values: in case they are coming from server
        const initial = initialValues
            ? Resolver.createValues(schema, { ...initialValues })
            : initialValues

        date.setMinutes(0, 0, 0) // Set to the beginning of the hour

        return createContextValues({
            values: {
                defaults: {
                    datetime: date.toISOString(),
                },
                initial: initialValues,
                current: computeInitialCurrentValues(initial, Store),
            },
        })
    }, [initialValues, Store, schema])
}
function computeInitialCurrentValues(initial, Store) {
    // That function is confusing. It handles the initial current values.
    // It has to deal with stored value and received initial values if any.

    if (!Store.has()) {
        return initial
    }

    const stored = Store.get()

    if (initial) {
        if (stored.submissionID === initial.submissionID) {
            return stored
        } else {
            return initial
        }
    } else {
        return 'submissionID' in stored ? undefined : stored
    }
}
